import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Typography } from "@mui/material";
import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { createWalletClient, custom, type Address, type Hex } from "viem";
import { appChain } from "@/helper/viem";
import useWallet from "@/hook/useWallet";
import Logo from "@/shared/ui/Logo";
import { saveTermsAgreement, termsAgreementMessage, TERMS_VERSION, type TermsDocument } from "./termsAgreement";
import useStyles from "./TermsModalContent.styles";

type Status = "loading" | "required" | "accepted" | "error";
type TermsSection = { number: number; title: string; paragraphs: string[] };

function parseTerms(text: string): { preamble: string; sections: TermsSection[] } {
  const normalized = text.replace(/\r\n/g, "\n");
  const headings = [...normalized.matchAll(/^(\d+)\. (.+)$/gm)];
  return {
    preamble: normalized.slice(0, headings[0]?.index ?? normalized.length).trim(),
    sections: headings.map((heading, index) => ({
      number: Number(heading[1]),
      title: heading[2],
      paragraphs: normalized.slice((heading.index ?? 0) + heading[0].length, headings[index + 1]?.index ?? normalized.length)
        .trim().split(/\n\s*\n/).filter(Boolean),
    })),
  };
}

function displayParagraph(paragraph: string, definition: boolean) {
  if (!definition) return paragraph;
  const termEnd = paragraph.search(/ (?:means|refers to) /);
  return termEnd > 0 ? <><strong>{paragraph.slice(0, termEnd)}</strong>{paragraph.slice(termEnd)}</> : paragraph;
}

const TermsModalContent = ({ account, status, terms, loadError, onAccepted, onClose, onRetry }: {
  account: string;
  status: Status;
  terms: TermsDocument | null;
  loadError: string;
  onAccepted: () => void;
  onClose: () => void;
  onRetry: () => void;
}) => {
  const { classes } = useStyles();
  const { wallet, connectWallet } = useWallet();
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [signature, setSignature] = useState<Hex | null>(null);
  const [activeSection, setActiveSection] = useState(1);
  const readerRef = useRef<HTMLDivElement>(null);
  const content = useMemo(() => parseTerms(terms?.text ?? ""), [terms?.text]);
  useEffect(() => {
    setChecked(false);
    setSignature(null);
    setError("");
  }, [account, terms?.hash]);

  const signAgreement = async () => {
    if (!checked || !account || !wallet?.provider || !terms || busy) return;
    setBusy(true);
    setError("");
    try {
      const client = createWalletClient({ account: account as Address, chain: appChain, transport: custom(wallet.provider) });
      const signed = signature || await client.signMessage({ account: account as Address, message: termsAgreementMessage(terms, account) });
      setSignature(signed);
      await saveTermsAgreement(terms, account, signed);
      onAccepted();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not sign the terms");
    } finally {
      setBusy(false);
    }
  };

  const jumpToSection = (number: number) => {
    const reader = readerRef.current;
    const target = reader?.querySelector<HTMLElement>(`#terms-section-${number}`);
    if (!reader || !target) return;
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    reader.scrollTo({ top: reader.scrollTop + target.getBoundingClientRect().top - reader.getBoundingClientRect().top - 24, behavior: reducedMotion ? "auto" : "smooth" });
    setActiveSection(number);
  };

  const updateActiveSection = () => {
    const reader = readerRef.current;
    if (!reader) return;
    const threshold = reader.getBoundingClientRect().top + 80;
    let visible = 1;
    reader.querySelectorAll<HTMLElement>("[data-terms-section]").forEach((section) => {
      if (section.getBoundingClientRect().top <= threshold) visible = Number(section.dataset.termsSection);
    });
    setActiveSection(visible);
  };

  return (
    <div className={classes.root}>
      <header className={classes.header}>
        <div className={classes.eyebrow}><Logo classNames={classes.logo} /><span>MAFIA <span aria-hidden="true">·</span> TERMS OF SERVICE <span aria-hidden="true">·</span> VERSION {terms?.version ?? TERMS_VERSION}</span></div>
        <div className={classes.titleRow}>
          <div>
            <Typography component="h1" className={classes.title}>Terms of Service</Typography>
            <Typography className={classes.intro}>{status === "accepted" ? "Review the current terms for Playmafia.io." : "Please review the terms for Playmafia.io before entering the game."}</Typography>
          </div>
          {status === "accepted" && <Button className={classes.closeButton} onClick={onClose}>Close <span aria-hidden="true">×</span></Button>}
        </div>
        {status !== "accepted" && <div className={classes.agreement}>
          <div className={classes.agreementMain}>
            <FormControlLabel
              control={<Checkbox checked={checked} disabled={!terms || status === "error"} onChange={(event) => setChecked(event.target.checked)} />}
              label="I agree to these Terms of Service"
            />
            <span className={classes.agreementNote}>Wallet message signature · no gas fee</span>
          </div>
          <div className={classes.actions}>
            {!account ? <Button variant="contained" onClick={() => void connectWallet().catch((cause) => setError(cause instanceof Error ? cause.message : "Could not connect wallet"))}>Connect wallet to agree</Button> :
              <Button variant="contained" disabled={!checked || !terms || !wallet?.provider || busy || status === "loading" || status === "error"} onClick={() => void signAgreement()}>{busy ? "Saving agreement…" : signature ? "Save signature" : "Agree and sign"}</Button>}
            {status === "loading" && <CircularProgress size={20} aria-label="Checking agreement" />}
            {status === "error" && <Button onClick={onRetry}>Retry loading</Button>}
          </div>
        </div>}
        {account && terms && !checked && status === "required" && <Typography className={classes.hint}>Check the box above to enable “Agree and sign”.</Typography>}
        {account && terms && !wallet?.provider && <Typography className={classes.hint}>Reconnect your wallet to sign.</Typography>}
        {(error || loadError) && <Typography role="alert" className={classes.error}>{error || loadError}</Typography>}
      </header>

      <div className={classes.body}>
        <nav className={clsx(classes.index, "native-scrollbar")} aria-label="Terms sections">
          <div className={classes.indexLabel}>CONTENTS <span>{String(content.sections.length).padStart(2, "0")} SECTIONS</span></div>
          {content.sections.map((section) => <button key={section.number} type="button" className={clsx(classes.indexItem, activeSection === section.number && classes.indexItemActive)} aria-current={activeSection === section.number ? "location" : undefined} onClick={() => jumpToSection(section.number)}><span>{String(section.number).padStart(2, "0")}</span>{section.title}</button>)}
        </nav>
        <div className={classes.readingArea}>
          <div className={classes.mobileIndex}>
            <label htmlFor="terms-section-select">JUMP TO SECTION</label>
            <select id="terms-section-select" value={activeSection} onChange={(event) => jumpToSection(Number(event.target.value))}>
              {content.sections.map((section) => <option value={section.number} key={section.number}>{String(section.number).padStart(2, "0")} · {section.title}</option>)}
            </select>
          </div>
          <div className={clsx(classes.reader, "native-scrollbar")} ref={readerRef} onScroll={updateActiveSection} tabIndex={0} aria-label="Terms of Service document">
            {!terms && !loadError && <Box className={classes.loading}><CircularProgress aria-label="Loading Terms of Service" /></Box>}
            {terms && <article className={classes.document}>
              <div className={classes.cover}>
                <div className={classes.coverRule} />
                <p>OFFICIAL DOCUMENT <span>·</span> PLAYMAFIA.IO</p>
                <h2>{content.preamble.split("\n")[0]}</h2>
                <pre>{content.preamble.split("\n").slice(1).join("\n").trim()}</pre>
              </div>
              {content.sections.map((section) => <section className={classes.section} id={`terms-section-${section.number}`} data-terms-section={section.number} key={section.number} aria-labelledby={`terms-heading-${section.number}`}>
                <div className={classes.sectionHeading}><span aria-hidden="true">{String(section.number).padStart(2, "0")}</span><h3 id={`terms-heading-${section.number}`}>{section.title}</h3></div>
                <div className={classes.sectionCopy}>{section.paragraphs.map((paragraph, index) => <p className={clsx(classes.paragraph, section.number === 1 && index > 0 && classes.definition, paragraph.startsWith("END OF TERMS OF SERVICE") && classes.endParagraph)} key={index}>{displayParagraph(paragraph, section.number === 1 && index > 0)}</p>)}</div>
              </section>)}
            </article>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsModalContent;
